import type { FileItem, Folder } from '@types';
import { slugify } from '@/utils/string';

import type { FileSearchItem } from './types';
import { MAX_SEARCH_RESULTS } from './constants';

// construct a stack of folders from slugified file names
export const getFolderStackFromPath = (
  pathStack: string[],
  rootFolder: Folder
): Folder[] => {
  const folderStack = [rootFolder];

  // eslint-disable-next-line no-restricted-syntax
  for (const folderName of pathStack) {
    if (!folderName) {
      return folderStack;
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const fileItem of folderStack[folderStack.length - 1].files) {
      if (
        fileItem.type === 'folder' &&
        (fileItem.hubspotId === folderName ||
          slugify(fileItem.name) === folderName)
      ) {
        folderStack.push(fileItem as Folder);
        break;
      }
    }
  }

  return folderStack;
};

export const getPathFromFolderStack = (folderStack: Folder[]): string =>
  folderStack
    .map((folder) =>
      folder.hubspotId ? folder.hubspotId : slugify(folder.name)
    )
    .join('/');

export const isPathFromStack = (
  pathStack: string[],
  folderStack: Folder[]
): boolean => getPathFromFolderStack(folderStack) === pathStack.join('/');

export const buildSearchMap = (folder: Folder, path = '') => {
  const searchMap: FileSearchItem[] = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const item of folder.files) {
    const searchItem = {
      ...item,
      path: item.type === 'folder' ? `${path}/${slugify(item.name)}` : path,
    };

    if (item.type === 'folder') {
      delete searchItem.files;
    }

    searchMap.push(searchItem);
  }

  // add the folder contents after the initial top-level items so we get a breadth-first search
  folder.files
    .filter((item) => item.type === 'folder')
    .forEach((item) => {
      searchMap.push(
        ...buildSearchMap(item as Folder, `${path}/${slugify(item.name)}`)
      );
    });

  return searchMap;
};

export const searchFiles = (
  searchMap: FileSearchItem[],
  query: string,
  isClient?: boolean,
  limit = MAX_SEARCH_RESULTS
): FileSearchItem[] => {
  const splitQuery = query.toLowerCase().split(' ');

  const hits = searchMap.filter((item) => {
    const companyNameInPath = item.path.split('/')[1];
    return splitQuery.every(
      (string) =>
        item.name.toLowerCase().includes(string.toLowerCase()) ||
        (companyNameInPath.toLowerCase().includes(string.toLowerCase()) &&
          item.type === 'file' &&
          !isClient)
    );
  });

  return hits.slice(0, limit);
};

export const clipLastFolder = (path: string) => {
  const pathStack = path.split('/');

  if (pathStack.length > 1) {
    pathStack.pop();
  }

  return pathStack.join('/');
};

export const isRootFolder = (folder: Folder) => folder.hubspotId !== undefined;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const shouldDisplayFileItem = (auth: any, item: FileItem) => {
  const filesLength = item.files ? item.files.length : 0;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
  const isClientOrInvestor = auth?.isClient;
  return !(isClientOrInvestor && item.type === 'folder' && filesLength <= 0);
};
